import { List } from 'immutable';
import { get } from './get';

/**
 * Filter the result set down to only available products
 * @param {Boolean} filterAvailable True or False
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 */
const filterByAvailable = (filterAvailable = true, product) => {
    return product.getIn(['productMeta', 'available']) === filterAvailable;
};

/**
 * Filter the result set down to products that have monthlyAvailable: true.
 * Alternatively, if a future vertical doesn't provide {frequency}Available: true|false on its price object,
 * it will return true regardless.
 * @param {String} frequency monthly|annual|fortnightly etc.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 */
const filterByFrequencyAvailable = (frequency, product) => {
    const priceAvailable = product.getIn(['productMeta', 'price', `${frequency}Available`]);
    return priceAvailable === true || priceAvailable === undefined;
};

/**
 * Filter values are what is 'included' in the result set.
 * @param {Array} filterValues What to include in the list of filtered products.
 * By default, all brands that are returned are "selected" and populated in the filter.
 * E.G. If filter.brands contains: [BUDD,WOOL], it should only show Budget and Wooloworths brands.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 */
const filterByBrandCode = (filterValues, product) => {
    // If you filter out all brands, it should show no results for filters.
    if (filterValues.isEmpty()) return false;
    // All brands are checked by default
    return filterValues.includes(product.getIn(['providerMeta', 'code']));
};

/**
 * Filter based on specific features that are 'Y' values i.e. 'Yes'.
 * @param {Array} filterValues What to include in the list of filtered products.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 */
const filterByFeature = (filterValues, product) => {
    if (filterValues.isEmpty()) return true;
    // Check all features and pop their response onto an array
    // If that array includes a false anywhere, we want to return false, so that it filters out.
    return !filterValues
        .map((filterStateKey) => {
            return get(product, `productMeta.${filterStateKey}`, 'N') === 'Y';
        })
        .includes(false);
};

/**
 * Filter to ONLY the products selected.
 * @param {Array} filterValues What to include in the list of filtered products.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 */
const filterByProductId = (filterValues, product) => {
    // If no products selected, show all.
    if (filterValues.isEmpty()) return true;
    return _filterByProductId(filterValues, product);
};

/**
 * Filter out the products in the list.
 * @param {Array} filterValues What to include in the list of filtered products.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to remove the product in the list. Returns false if we want to keep it.
 */
const filterOutByProductId = (filterValues, product) => {
    if (filterValues.isEmpty()) return true;
    return !_filterByProductId(filterValues, product);
};

/**
 * A helper method to filter products out by ID.
 * @param {Array} filterValues What to include in the list of filtered products.
 * @param {Object} product A single product
 * @returns {Boolean} Returns true if we want to keep the product in the list. Returns false if we want to remove it.
 * @private
 */
const _filterByProductId = (filterValues, product) => {
    return filterValues.includes(product.getIn(['productMeta', 'id']));
};

/**
 * Maintain a map of filter keys to filter methods.
 * These are the filters currently supported by the API. Use these as the 'filter' in the schema.
 * @type {Object}
 */
const filterMap = {
    frequency: filterByFrequencyAvailable,
    available: filterByAvailable,
    brands: filterByBrandCode,
    features: filterByFeature,
    productIdsIncludeOnly: filterByProductId, // For pagination??? if we do results by filtering by page
    selectedProductIds: filterByProductId, // this would need to unset other product id filters in future
    productIdsExclude: filterOutByProductId // For 'x'ing out a product
};

/**
 * The main method called by reselect to filter the array of products down to what is selected in the
 * filters state.
 * @param {Object} filteringConfig The results.config.filters map
 * @param {Object} iterableObj The product list
 * @returns {Array} The array of filtered products
 */
const filterProducts = (filteringConfig, iterableObj) => {
    if (!List.isList(iterableObj)) {
        throw Error('Filter Methods: Product List is not in proper type');
    }
    let productArray = [...iterableObj];
    if (!filteringConfig.keySeq()) {
        return productArray;
    }
    // For each filter type, filter the products out that do not match.
    // Filters are an 'AND' type logic, meaning if it doesn't match one type of filter, it is excluded from the next
    // set of filters.
    filteringConfig
        .keySeq()
        .toArray()
        .forEach((filterName) => {
            productArray = productArray.filter((product) => {
                // If there is no mapping to a method, assume it has no impact on results
                if (!filterMap[filterName]) {
                    return true;
                }
                return filterMap[filterName](filteringConfig.get(filterName), product);
            });
        });

    return productArray;
};

/**
 * Gets the products that have been filtered out of the results set
 *
 * @param {Object} filteringConfig The results.config.filters map
 * @param {Object} iterableObj The product list
 * @returns {Array} Array of objects in the form { quote: {}, filter: filterName (where filterName
 *      is the name of the filter that caused it to be omitted).
 */
const filteredOutQuotes = (filteringConfig, iterableObj) => {
    if (!List.isList(iterableObj)) {
        throw Error('Filter Methods: Product List is not in proper type');
    }
    const productArray = [...iterableObj];
    if (!filteringConfig.keySeq()) {
        return [];
    }

    // For each filter type, filter the products out that do not match.
    // Filters are an 'AND' type logic, meaning if it doesn't match one type of filter, it is excluded from the next
    // set of filters.
    const filtered = [];
    filteringConfig
        .keySeq()
        .toArray()
        .forEach((filterName) => {
            if (filterName === 'available') return;
            if (!filterMap[filterName]) return;
            productArray.forEach((product) => {
                if (!filterMap[filterName](filteringConfig.get(filterName), product)) {
                    filtered.push({
                        quote: product,
                        filter: filterName
                    });
                }
            });
        });

    return filtered;
};

export { filterProducts, filteredOutQuotes };
